import {
  isAxiosError,
} from 'axios';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';

import {
  formatDate,
  formatDateOnly,
} from 'utils/date';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  isEmptyString,
  notEmptyString,
  setDefaultValueIf,
} from 'utils/misc';
import {
  InsuranceDTO,
  PatientDTO,
  InsuranceRelationshipTypeSettingDTO,
  AddressDTO,
  InsuranceMSPTypeSettingDTO,
  PayerEntryDTO,
} from '@/dtos';
import {
  InsuranceDataForm,
  REQUIRED_ADDRESS_FIELDS,
} from '@/components/InsuranceForm';
import values from 'lodash/values';
import pick from 'lodash/pick';
import {
  FilterOptionsState,
} from '@mui/material/useAutocomplete';
import {
  Patient,
} from '@/types/patient';
import {
  getAddressString,
  getFullName,
} from './string';

export type Payer = Pick<PayerEntryDTO, 'payerEntryId' | 'payerName' | 'address' | 'insuranceType' | 'isFavorited' | 'isPaper' | 'payerId' | 'searchTerms'>;

type ApiErrorResponse = {
  message?: unknown;
  Message?: unknown;
  error?: unknown;
  Error?: unknown;
  detail?: unknown;
  title?: unknown;
};

export function getApiErrorMessage(error: unknown, fallback: string) {
  if (!isAxiosError<ApiErrorResponse | string>(error)) {
    return fallback;
  }

  const errorData = error.response?.data;

  if (typeof errorData === 'string' && errorData.trim() !== '') {
    return errorData;
  }

  if (!isNil(errorData) && typeof errorData === 'object') {
    const message = errorData.message
      ?? errorData.Message
      ?? errorData.error
      ?? errorData.Error
      ?? errorData.detail
      ?? errorData.title;

    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
  }

  return fallback;
}

export type InsuranceFormInfo = Omit<InsuranceDTO,
  'insuranceOwnerDOB'
  | 'mspType'
  | 'effectiveFrom'
  | 'effectiveTill'
> & {
  mspType?: InsuranceMSPTypeSettingDTO;
  insuranceOwnerDOB?: Dayjs;
  effectiveFrom?: Dayjs;
  effectiveTill?: Dayjs | null;
};

export function getPatientAddress(address: Partial<AddressDTO>) {
  return omit(address, ['nickName', 'serviceFacilityName', 'serviceFacilityNpi']);
}

const EMAIL_REGEX = /^(?:[A-Za-z0-9](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*[A-Za-z0-9])?)*|"[^"]+")@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
export const PATIENT_NAME_REGEX = /^[\p{L}\p{M}\p{N}]+(?:[ '\u2019-][\p{L}\p{M}\p{N}]+)*$/u;

export function normalizePatientName(value?: string | null): string {
  return value?.trim() ?? '';
}

export function validatePatientName(
  value: string | null | undefined,
  message: string,
  required: boolean = true,
): true | string {
  const trimmed = normalizePatientName(value);
  if (isEmptyString(trimmed)) {
    return required ? message : true;
  }
  return PATIENT_NAME_REGEX.test(trimmed) ? true : message;
}

export function normalizePhoneNumber(phone: string) {
  const phoneNumber = phone?.replace(/\D/g, '');
  return notEmptyString(phoneNumber) && phoneNumber?.length === 11
    ? `+${phoneNumber}` : undefined;
}
export function normalizeNumber(number: string) {
  return number?.replace(/\D/g, '');
}

export const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email);
export const validatePhone = (phone?: string | null): boolean => {
  const phoneNumber = normalizePhoneNumber(phone ?? '');
  return /^\+\d{11}$/i.test(phoneNumber ?? '');
};

export const validateSSN = (
  ssn?: string | null,
): boolean => isEmptyString(ssn) || ssn?.length === 9;

export const validateEmailIfSet = (email: string = '') => isEmptyString(email) || validateEmail(email);
export const validatePhoneIfSet = (phone: string | null = '') => {
  const phoneNumber = normalizeNumber(phone ?? '') ?? '';
  return phoneNumber?.substring(1).length === 0 || validatePhone(phoneNumber);
};
export const validateDateRange = (dateRange: Dayjs[]) => (
  dateRange[0]?.isBefore(dateRange[1]) ?? false
);

export function validatePatient(patient: PatientDTO) {
  return isNil(patient?.patientId) ? 'Select patient' : true;
}

export function normalizeInsurance(insurance: InsuranceFormInfo): InsuranceDTO {
  const insuranceInfo = omit(insurance, ['insuranceOwnerDOB', 'effectiveFrom', 'effectiveTill']);
  return {
    ...(insuranceInfo ?? {}),
    ...setDefaultValueIf(
      insuranceInfo ?? {},
      null,
      isEmptyString,
    ),
    mspType: insurance?.mspType?.code,
    inactive: false,
    insuranceSlot: insurance.insuranceSlot,
    insuranceOwnerDOB: formatDateOnly(insurance?.insuranceOwnerDOB ?? dayjs()),
    insuranceOwnerPhoneNumber: normalizePhoneNumber(insurance?.insuranceOwnerPhoneNumber ?? ''),
    effectiveFrom: formatDateOnly(insurance?.effectiveFrom ?? dayjs()),
    effectiveTill: null,
  };
}

export function serializeInsurance(
  insurance: InsuranceDataForm,
  patient: PatientDTO,
  insuranceOwnerRelationshipType: InsuranceRelationshipTypeSettingDTO,
) {
  const { isSubscriberPatient, isEmployerInsurance } = insuranceOwnerRelationshipType;
  const isSelf = isSubscriberPatient || isEmployerInsurance;
  const insuranceInfo = omit(insurance, ['insuranceOwnerDOB', 'effectiveFrom', 'effectiveTill', 'demographics']);
  const { demographics } = insurance ?? {};
  const hasPhone = notEmptyString(patient?.phoneNumber?.phoneNumberWithAreaCode);
  const insuranceOwnerPhoneNumber = isSelf && hasPhone
    ? patient?.phoneNumber?.phoneNumberWithAreaCode
    : insurance.insuranceOwnerPhoneNumber;

  const hasInputAddress = values(
    pick(insurance.insuranceOwnerAddress, REQUIRED_ADDRESS_FIELDS),
  ).some((item) => !isEmptyString(item));

  const inputAddress = hasInputAddress
    ? insurance.insuranceOwnerAddress
    : undefined;

  const hasAddress = !isNil(patient?.address) && values(
    pick(patient?.address, REQUIRED_ADDRESS_FIELDS),
  ).every((item) => !isEmptyString(item));

  const insuranceOwnerAddress = (
    isSelf && hasAddress
      ? getPatientAddress(patient?.address ?? {})
      : inputAddress
  );

  const insuranceOwnerAddressId = (
    isSelf && hasAddress
      ? patient?.address?.addressId
      : insurance?.insuranceOwnerAddressId
  ) ?? 0;

  return {
    ...(insuranceInfo ?? {}),
    ...setDefaultValueIf(
      insuranceInfo ?? {},
      null,
      isEmptyString,
    ),
    mspType: insurance?.mspType?.code,
    inactive: false,
    insuranceSlot: insurance.insuranceSlot,
    insuranceOwnerDOB: formatDateOnly(demographics?.dateOfBirth ?? dayjs()),
    insuranceOwnerPhoneNumber: normalizePhoneNumber(insuranceOwnerPhoneNumber ?? ''),
    insuranceOwnerFirstName: normalizePatientName(demographics?.firstName),
    insuranceOwnerMiddleName: normalizePatientName(demographics?.middleName),
    insuranceOwnerLastName: normalizePatientName(demographics?.lastName),
    insuranceOwnerGenderDescription: demographics?.genderDescription,
    effectiveFrom: formatDateOnly(insurance?.effectiveFrom ?? dayjs()),
    effectiveTill: null,
    insuranceOwnerAddressId: isNil(insuranceOwnerAddress)
      ? null
      : insuranceOwnerAddressId,
    insuranceOwnerAddress: insuranceOwnerAddress ?? null,
  } as InsuranceDTO;
}

export const validateDateIfFuture = (date?: Dayjs) => !date?.isAfter(formatDate(dayjs()), 'date') && date?.isValid();
export const validateDate = (date?: Dayjs) => !isNil(date) && dayjs(date)?.isValid();

function getFinalizedNoteCaption(fullName: string, fileName: string = '') {
  const [, date] = fileName.match(/^\d+-visit-(\d{4}-\d{2}-\d{2})/i) ?? [];
  return typeof date !== 'undefined' ? `${fullName} Visit on ${formatDate(date)}` : undefined;
}

export function filterAutocompleteOptions<T>(
  options: T[],
  state: FilterOptionsState<T>,
  getSearchableTexts: (option: T) => string[],
) {
  const { inputValue } = state;
  const searchRegex = new RegExp(inputValue.replaceAll('\\', '\\\\'), 'i');
  return isEmptyString(inputValue)
    ? options
    : options.filter((option: T) => {
      const texts = getSearchableTexts(option);
      return texts.some((text) => searchRegex.test(text));
    });
}

export function filterInsurances(options: Payer[], state: FilterOptionsState<Payer>) {
  return filterAutocompleteOptions<Payer>(
    options,
    state,
    (option) => ([option?.payerName ?? '', ...(option?.searchTerms?.split(' ').filter(notEmptyString) ?? [])]),
  );
}

export function normalizePatient(patient: PatientDTO): Patient {
  return {
    ...patient,
    patientId: patient?.patientId ?? 0,
    fullName: patient ? getFullName(patient) : 'Developer',
    formattedAddress: getAddressString(patient?.address),
  };
}

export const parseErrorMessage = (error: unknown, defaultMsg = 'Something went wrong.'): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as any).response?.data;
    if (typeof data === 'string' && !isEmptyString(data)) {
      return data;
    }
    if (data && typeof data === 'object') {
      const { errors, detail, message, title } = data as Record<string, any>;
      if (errors && typeof errors === 'object') {
        const list = values(errors).flat().filter(Boolean);
        if (list.length > 0) return list.map(String).join(' ');
      }
      const text = detail ?? message ?? title;
      if (typeof text === 'string' && !isEmptyString(text)) return text;
    }
  }
  if (error instanceof Error && typeof error.message === 'string' && !isEmptyString(error.message)) {
    return error.message;
  }
  return typeof error === 'string' && !isEmptyString(error) ? error : defaultMsg;
};

export function normalizeInteractiveFieldValues(data: Record<string, any>): any[] {
  const result: any[] = [];
  const entries = Object.entries(data);
  for (const [key, value] of entries) {
    if (value && typeof value === 'object' && 'type' in value && key.includes(':')) {
      const typedField = value as any;
      const formId = typedField.formId ?? 0;

      if (typedField?.type === 'radioGroup' && Array.isArray(typedField.children) && typedField.value) {
        const match = typedField.children.find((c: any) => c.id === typedField.value);
        result.push({
          formId,
          fieldId: match?.id?.toString() ?? '',
          value: '✔',
          type: 'Text',
        });
        continue;
      }

      if (typedField?.type === 'checkbox') {
        result.push({
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: typedField.value === true ? '✔' : '',
          type: 'Text',
        });
        continue;
      }
      if (typedField?.type === 'dollar' && !isEmptyString(typedField?.value)) {
        result.push({
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: `$${typedField?.value}`,
          type: 'Text',
        });
        continue;
      }
      if (typedField?.type === 'percent' && !isEmptyString(typedField?.value)) {
        result.push({
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: `${typedField?.value}%`,
          type: 'Text',
        });
        continue;
      }

      const rawValue = typedField?.value?.toString() ?? '';
      const isSignatureField = typedField?.type === 'signature' || typedField?.type === 'initial';
      const isImage = isSignatureField && /^data:image\//i.test(rawValue);

      result.push({
        formId,
        fieldId: typedField.id?.toString() ?? '',
        value: rawValue,
        type: isImage ? 'ImageDataURI' : 'Text',
      });
    }
  }
  return result;
}

export function serializeFormTemplateMetaData(
  data: any,
): any {
  return {
    ...data,
    renderingData: (data.renderingData ?? []).map((template: any) => ({
      ...template,
      value: '',
      fields: template.fields?.map((field: any) => ({
        ...field,
        type: field.type ?? 'ShortText',
      })) ?? [],
    })),
  };
}
